import { TimeRange } from '../types';

export interface RangeValidationError {
  index: number;
  type: 'invalid_range' | 'overlap' | `empty_range` | `less_than_duration`;
  message: string;
}

const timeToMins = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

// Length of a range in minutes, treating an end time of 23:59 (12:00 AM) as 24:00.
const rangeMinutes = (range: TimeRange): number => {
  const [sH, sM] = range.start_time.split(':').map(Number);
  const [eH, eM] = range.end_time.split(':').map(Number);
  const endMins = eH === 23 && eM === 59 ? 24 * 60 : eH * 60 + eM;
  return endMins - (sH * 60 + sM);
};

const headRangeMinutes = (ranges?: TimeRange[] | null): number | null => {
  const r = ranges?.find((x) => x.start_time === '00:00' && x.end_time);
  return r ? rangeMinutes(r) : null;
};

const tailRangeMinutes = (ranges?: TimeRange[] | null): number | null => {
  const r = ranges?.find((x) => x.start_time && x.end_time === '23:59');
  return r ? rangeMinutes(r) : null;
};

/**
 * Durations (in minutes) of the ranges a midnight-spanning slot wraps into/out of.
 * - `partnerHeadMinutes`: the 12:00 AM (00:00) range this array's 12:00 AM-ending tail continues into.
 * - `partnerTailMinutes`: the 12:00 AM-ending range that continues into this array's 12:00 AM head.
 * When omitted, validateTimeRanges pairs within the same array (global mode, schedule repeats daily).
 */
export interface WrapPartners {
  partnerHeadMinutes?: number | null;
  partnerTailMinutes?: number | null;
}

export interface RangeSlotInfo {
  totalMinutes: number;
  slots: number;
  remainder: number;
}

interface RangeSlotBlock {
  minutes: number;
  indexes: number[];
}

export const getWrapPartnersForRanges = (ranges?: TimeRange[] | null): WrapPartners => ({
  partnerHeadMinutes: headRangeMinutes(ranges),
  partnerTailMinutes: tailRangeMinutes(ranges),
});

const getMinutesNeededToCompleteSlot = (minutes: number, duration: number): number => {
  if (!duration || minutes <= 0) return 0;
  const remainder = minutes % duration;
  return remainder === 0 ? 0 : duration - remainder;
};

const buildContinuousRangeBlocks = (ranges: TimeRange[]): RangeSlotBlock[] => {
  const blocks: RangeSlotBlock[] = [];
  const indexedRanges = ranges
    .map((range, index) => ({ range, index }))
    .filter(({ range }) => range.start_time && range.end_time)
    .map(({ range, index }) => ({ range, index, minutes: rangeMinutes(range) }))
    .filter(({ minutes }) => minutes > 0)
    .sort((a, b) => timeToMins(a.range.start_time) - timeToMins(b.range.start_time));

  indexedRanges.forEach(({ range, index, minutes }) => {
    const lastBlock = blocks[blocks.length - 1];
    const previousRange = lastBlock
      ? ranges[lastBlock.indexes[lastBlock.indexes.length - 1]]
      : null;

    if (lastBlock && previousRange?.end_time === range.start_time) {
      lastBlock.minutes += minutes;
      lastBlock.indexes.push(index);
      return;
    }

    blocks.push({ minutes, indexes: [index] });
  });

  return blocks;
};

const getRangeBlockMinutes = (
  range: TimeRange,
  duration: number,
  wrap?: WrapPartners,
): number | null => {
  if (!range.start_time || !range.end_time) return null;

  const ownMinutes = rangeMinutes(range);
  if (ownMinutes <= 0) return null;

  if (range.end_time === '23:59' && wrap?.partnerHeadMinutes != null) {
    return ownMinutes + Math.min(
      wrap.partnerHeadMinutes,
      getMinutesNeededToCompleteSlot(ownMinutes, duration),
    );
  }

  if (range.start_time === '00:00' && wrap?.partnerTailMinutes != null) {
    const consumedByPreviousDay = Math.min(
      ownMinutes,
      getMinutesNeededToCompleteSlot(wrap.partnerTailMinutes, duration),
    );
    const remainingMinutes = ownMinutes - consumedByPreviousDay;
    return remainingMinutes > 0
      ? remainingMinutes
      : ownMinutes + wrap.partnerTailMinutes;
  }

  return ownMinutes;
};

export const isRangeSlotPerfectFit = (
  range: TimeRange,
  duration: number,
  wrap?: WrapPartners,
  ranges?: TimeRange[],
): boolean => {
  if (ranges) {
    const rangeIndex = ranges.indexOf(range);
    if (rangeIndex !== -1) {
      const block = buildContinuousRangeBlocks(ranges).find((b) => b.indexes.includes(rangeIndex));
      if (block && block.indexes.length > 1) {
        return !!duration && block.minutes > 0 && block.minutes % duration === 0;
      }
    }
  }

  const blockMinutes = getRangeBlockMinutes(range, duration, wrap);
  return !!duration && blockMinutes != null && blockMinutes > 0 && blockMinutes % duration === 0;
};

export const calculateRangeSlotInfo = (
  ranges: TimeRange[],
  duration: number,
  wrap?: WrapPartners,
): RangeSlotInfo => {
  if (!duration || !ranges || ranges.length === 0) {
    return { totalMinutes: 0, slots: 0, remainder: 0 };
  }

  const tailIdx = ranges.findIndex((r) => r.start_time && r.end_time === '23:59');
  const headIdx = ranges.findIndex((r) => r.start_time === '00:00' && r.end_time);
  const continuousBlocks = buildContinuousRangeBlocks(ranges);
  const skipIndexes = new Set<number>();
  const blocks: number[] = [];

  continuousBlocks.forEach((block) => {
    if (block.indexes.length > 1) {
      blocks.push(block.minutes);
      block.indexes.forEach((index) => skipIndexes.add(index));
    }
  });

  if (tailIdx !== -1 && !skipIndexes.has(tailIdx) && wrap?.partnerHeadMinutes != null) {
    const tailMinutes = rangeMinutes(ranges[tailIdx]);
    if (tailMinutes > 0) {
      const borrowedMinutes = Math.min(
        wrap.partnerHeadMinutes,
        getMinutesNeededToCompleteSlot(tailMinutes, duration),
      );
      blocks.push(tailMinutes + borrowedMinutes);
      skipIndexes.add(tailIdx);
    }
  }

  if (headIdx !== -1 && !skipIndexes.has(headIdx) && wrap?.partnerTailMinutes != null) {
    const headMinutes = rangeMinutes(ranges[headIdx]);
    if (headMinutes > 0) {
      const consumedByPreviousDay = Math.min(
        headMinutes,
        getMinutesNeededToCompleteSlot(wrap.partnerTailMinutes, duration),
      );
      const remainingHeadMinutes = headMinutes - consumedByPreviousDay;
      if (remainingHeadMinutes > 0) {
        blocks.push(remainingHeadMinutes);
      }
      skipIndexes.add(headIdx);
    }
  }

  ranges.forEach((range, index) => {
    if (skipIndexes.has(index)) return;
    const minutes = range.start_time && range.end_time ? rangeMinutes(range) : 0;
    if (minutes > 0) {
      blocks.push(minutes);
    }
  });

  return blocks.reduce<RangeSlotInfo>((acc, minutes) => ({
    totalMinutes: acc.totalMinutes + minutes,
    slots: acc.slots + Math.floor(minutes / duration),
    remainder: acc.remainder + (minutes % duration),
  }), { totalMinutes: 0, slots: 0, remainder: 0 });
};

/**
 * For per-day schedules a slot may span midnight: a range ending at 12:00 AM on one day continues
 * as a range starting at 12:00 AM on the next calendar day. Given the full per-day slots map and a
 * day (1=Mon … 7=Sun, wrapping Sun→Mon), returns the adjacent days' partner durations. An adjacent
 * day only counts as a partner when it is an active business day (`activeDays`); a range left in the
 * slots map for a toggled-off day must not satisfy a neighbour's midnight wrap.
 */
export const getPerDayWrapPartners = (
  slots: Record<string, TimeRange[] | null> | undefined,
  day: number,
  activeDays?: number[],
): WrapPartners => {
  const nextDay = day === 7 ? 1 : day + 1;
  const prevDay = day === 1 ? 7 : day - 1;
  const isActive = (d: number) => !activeDays || activeDays.includes(d);
  return {
    partnerHeadMinutes: isActive(nextDay) ? headRangeMinutes(slots?.[String(nextDay)]) : null,
    partnerTailMinutes: isActive(prevDay) ? tailRangeMinutes(slots?.[String(prevDay)]) : null,
  };
};

export const validateTimeRanges = (
  ranges: TimeRange[],
  duration?: number,
  wrap?: WrapPartners,
): RangeValidationError[] => {
  const errors: RangeValidationError[] = [];

  if (!ranges || !Array.isArray(ranges) || ranges.length === 0) {
    return errors;
  }

  // A range ending at 12:00 AM (23:59) may be shorter than the slot duration as long as it is
  // paired with a range starting at 12:00 AM (00:00) — together they form one slot that spans
  // midnight. The partner comes from the next/previous day (per-day, via `wrap`) or from this same
  // array (global mode). The two halves must sum to at least the slot duration.
  let wrapTailIndex = -1;
  let wrapHeadIndex = -1;
  if (duration != null) {
    const tailIdx = ranges.findIndex((r) => r.start_time && r.end_time === '23:59');
    const headIdx = ranges.findIndex((r) => r.start_time === '00:00' && r.end_time);

    const partnerHeadMins = wrap
      ? wrap.partnerHeadMinutes ?? null
      : headIdx !== -1 ? rangeMinutes(ranges[headIdx]) : null;
    const partnerTailMins = wrap
      ? wrap.partnerTailMinutes ?? null
      : tailIdx !== -1 ? rangeMinutes(ranges[tailIdx]) : null;

    if (
      tailIdx !== -1 &&
      partnerHeadMins != null &&
      rangeMinutes(ranges[tailIdx]) + partnerHeadMins >= duration
    ) {
      wrapTailIndex = tailIdx;
    }
    if (
      headIdx !== -1 &&
      partnerTailMins != null &&
      rangeMinutes(ranges[headIdx]) + partnerTailMins >= duration
    ) {
      wrapHeadIndex = headIdx;
    }
  }

  ranges.forEach((range, i) => {
    if (!range.start_time || !range.end_time){
      errors.push({
        index: i,
        type: `empty_range`,
        message: `Empty start or end time`
      })
    }
    if (range.start_time && range.end_time && range.start_time >= range.end_time) {
      errors.push({
        index: i,
        type: 'invalid_range',
        message: `End time must be after start time`,
      });
    }
    if (duration != null && range.start_time && range.end_time) {
      const [sH, sM] = range.start_time.split(':').map(Number);
      const [eH, eM] = range.end_time.split(':').map(Number);
      let endMins = eH * 60 + eM;
      if (eH === 23 && eM === 59) {
        endMins = 24 * 60;
      }
      const slotMins = endMins - (sH * 60 + sM);
      if (slotMins > 0 && slotMins < duration) {
        if (i === wrapTailIndex || i === wrapHeadIndex) {
          // valid midnight-wrap half — allowed to be shorter than the slot duration
        } else if (range.end_time === '23:59') {
          errors.push({
            index: i,
            type: 'less_than_duration',
            message: `A range ending at 12:00 AM must be paired with a range starting at 12:00 AM on the next day, and together they must cover at least the slot duration (${duration} min).`,
          });
        } else if (range.start_time === "00:00") {
          errors.push({
            index: i,
            type: 'less_than_duration',
            message: `A range starting at 12:00 AM must be paired with a range ending at 12:00 AM on the previous day, and together they must cover at least the slot duration (${duration} min).`,
          });
        }
        else {
          errors.push({
            index: i,
            type: 'less_than_duration',
            message: `Slot length (${slotMins} min) is less than duration (${duration} min)`,
          });
        }
      }
    }
  });

  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const a = ranges[i];
      const b = ranges[j];
      if (a.start_time && a.end_time && b.start_time && b.end_time) {
        const aStart = timeToMins(a.start_time);
        const aEnd = timeToMins(a.end_time);
        const bStart = timeToMins(b.start_time);
        const bEnd = timeToMins(b.end_time);

        if (aEnd > bStart && bEnd > aStart) {
          errors.push({ index: i, type: 'overlap', message: `Overlaps with range ${j + 1}` });
          errors.push({ index: j, type: 'overlap', message: `Overlaps with range ${i + 1}` });
        }
      }
    }
  }

  return errors;
};
